import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { getReleases, getCurrentRelease, getReleaseFeatures, createRelease, updateRelease, deleteRelease, getReleaseFeatureCount } from '../services/releaseService';
import { getFeatureVoteCounts, updateFeature } from '../services/featureService';
import { logger, handleError } from '../utils/logger';
import './Releases.css';

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString();
};

// Status options with numbers for display
const STATUS_OPTIONS = ['draft', 'planning', 'in progress', 'testing', 'beta', 'live'];
const STATUS_OPTIONS_WITH_NUMBERS = STATUS_OPTIONS.map((status, index) => ({
  value: status,
  label: `${index + 1}. ${status.charAt(0).toUpperCase() + status.slice(1)}`
}));

// Component for release row with expand/collapse and features
const ReleaseRow = ({ release, isOwner, onEdit, onDelete, onViewFeature, featureGroupId, defaultExpanded = false, userRole = null }) => {
  const [featureCount, setFeatureCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [features, setFeatures] = useState([]);
  const [loadingFeatures, setLoadingFeatures] = useState(false);
  const [voteCounts, setVoteCounts] = useState(new Map());

  useEffect(() => {
    const loadCount = async () => {
      try {
        const count = await getReleaseFeatureCount(release.id);
        setFeatureCount(count);
      } catch (err) {
        logger.error('Error loading feature count:', err);
      } finally {
        setLoading(false);
      }
    };
    loadCount();
  }, [release.id]);

  const loadFeatures = useCallback(async () => {
    if (loadingFeatures) return;
    setLoadingFeatures(true);
    try {
      const releaseFeatures = await getReleaseFeatures(release.id);
      setFeatures(releaseFeatures);
      
      // Load vote counts for features
      if (releaseFeatures.length > 0 && featureGroupId) {
        const featureIds = releaseFeatures.map(f => f.id);
        const counts = await getFeatureVoteCounts(featureIds, featureGroupId);
        setVoteCounts(counts);
      }
    } catch (err) {
      logger.error('Error loading release features:', err);
    } finally {
      setLoadingFeatures(false);
    }
  }, [release.id, featureGroupId, loadingFeatures]);

  useEffect(() => {
    if (defaultExpanded && features.length === 0 && !loadingFeatures) {
      loadFeatures();
    }
  }, [defaultExpanded, features.length, loadingFeatures, loadFeatures]);

  const handleToggleExpand = () => {
    const newExpanded = !expanded;
    setExpanded(newExpanded);
    if (newExpanded && features.length === 0) {
      loadFeatures();
    }
  };

  const handleReleaseReadyChange = async (featureId, checked) => {
    try {
      await updateFeature(featureId, { release_ready: checked });
      // Reload features to get updated data
      await loadFeatures();
    } catch (err) {
      logger.error('Error updating release ready status:', err);
    }
  };

  const totalVotes = useMemo(() => {
    let total = 0;
    voteCounts.forEach(count => {
      if (count && typeof count === 'object') {
        total += count.total || 0;
      }
    });
    return total;
  }, [voteCounts]);

  return (
    <>
      <tr>
        <td>
          <button
            type="button"
            className="expand-button"
            onClick={handleToggleExpand}
            title={expanded ? 'Collapse' : 'Expand'}
          >
            <i className={`fas fa-chevron-${expanded ? 'down' : 'right'}`}></i>
          </button>
          {release.name}
        </td>
        <td>
          <span className={`release-status-badge status-${release.status.replace(/\s+/g, '-')}`}>
            {STATUS_OPTIONS_WITH_NUMBERS.find(opt => opt.value === release.status)?.label || release.status}
          </span>
        </td>
        <td>
          {formatDate(release.start_date)} {release.end_date && `- ${formatDate(release.end_date)}`}
        </td>
        <td>{loading ? '...' : featureCount}</td>
        {isOwner && (
          <td>
            <div className="release-actions">
              <button
                type="button"
                className="icon-button"
                onClick={() => onEdit(release)}
                title="Edit release"
              >
                <i className="fas fa-edit"></i>
              </button>
              <button
                type="button"
                className="icon-button delete-button"
                onClick={() => onDelete(release.id)}
                title="Delete release"
              >
                <i className="fas fa-trash"></i>
              </button>
            </div>
          </td>
        )}
      </tr>
      {expanded && (
        <tr>
          <td colSpan={isOwner ? 5 : 4} className="release-features-cell">
            {loadingFeatures ? (
              <div className="loading-features">Loading features...</div>
            ) : features.length === 0 ? (
              <div className="no-features">No features in this release</div>
            ) : (
              <table className="release-features-table">
                <thead>
                  <tr>
                    <th>Feature Name</th>
                    <th>Status</th>
                    <th>Votes</th>
                    {userRole !== 'Guest' && <th>Release Ready</th>}
                  </tr>
                </thead>
                <tbody>
                  {features.map(feature => {
                    const votes = voteCounts.get(feature.id)?.total || 0;
                    return (
                      <tr key={feature.id} onClick={() => onViewFeature && onViewFeature(feature)} className="feature-row-clickable">
                        <td>{feature.name}</td>
                        <td>
                          <span className={`status-pill status-${(feature.status || 'No Status').toLowerCase().replace(/\s+/g, '-')}`}>
                            {feature.status || 'No Status'}
                          </span>
                        </td>
                        <td>{votes}</td>
                        {userRole !== 'Guest' && (
                          <td>
                            <input
                              type="checkbox"
                              checked={feature.release_ready || false}
                              onChange={(e) => {
                                e.stopPropagation();
                                handleReleaseReadyChange(feature.id, e.target.checked);
                              }}
                            />
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="total-votes-row">
                    <td colSpan={userRole !== 'Guest' ? "3" : "2"}><strong>Total Votes:</strong></td>
                    <td><strong>{totalVotes}</strong></td>
                  </tr>
                </tfoot>
              </table>
            )}
          </td>
        </tr>
      )}
    </>
  );
};

// Memoize ReleaseRow component to prevent unnecessary re-renders when props haven't changed
const MemoizedReleaseRow = React.memo(ReleaseRow);

const Releases = ({ featureGroupId, userRole, onViewFeature }) => {
  const [releases, setReleases] = useState([]);
  const [currentRelease, setCurrentRelease] = useState(null);
  const [currentReleaseFeatures, setCurrentReleaseFeatures] = useState([]);
  const [voteCounts, setVoteCounts] = useState(new Map());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [editingRelease, setEditingRelease] = useState(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    internal_description: '',
    public_description: '',
    start_date: '',
    end_date: '',
    status: 'draft',
    current_subversion: '',
    comments: ''
  });

  const isOwner = userRole === 'Owner';

  // Define loadReleases and loadCurrentReleaseFeatures before the useEffect hooks that use them
  const loadReleases = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const allReleases = await getReleases(featureGroupId);
      
      // Sort releases by end_date (earliest first), with nulls last
      const sortedReleases = [...allReleases].sort((a, b) => {
        if (!a.end_date && !b.end_date) return 0;
        if (!a.end_date) return 1;
        if (!b.end_date) return -1;
        return new Date(a.end_date) - new Date(b.end_date);
      });
      
      setReleases(sortedReleases);
      
      // Get current release (where today is between start_date and end_date)
      const current = await getCurrentRelease(featureGroupId);
      setCurrentRelease(current);
    } catch (err) {
      setError(handleError(err, 'Failed to load releases. Please try again.'));
      logger.error(err);
    } finally {
      setLoading(false);
    }
  }, [featureGroupId]);

  const loadCurrentReleaseFeatures = useCallback(async () => {
    if (!currentRelease) return;
    try {
      const features = await getReleaseFeatures(currentRelease.id);
      setCurrentReleaseFeatures(features);
      
      // Load vote counts for features
      if (features.length > 0) {
        const featureIds = features.map(f => f.id);
        const counts = await getFeatureVoteCounts(featureIds, featureGroupId);
        setVoteCounts(counts);
      }
    } catch (err) {
      logger.error('Error loading current release features:', err);
    }
  }, [currentRelease, featureGroupId]);

  useEffect(() => {
    if (featureGroupId) {
      loadReleases();
    }
  }, [featureGroupId, loadReleases]);

  useEffect(() => {
    if (currentRelease) {
      loadCurrentReleaseFeatures();
    }
  }, [currentRelease, loadCurrentReleaseFeatures]);

  useEffect(() => {
    if (featureGroupId) {
      loadReleases();
    }
  }, [featureGroupId, loadReleases]);

  useEffect(() => {
    if (currentRelease) {
      loadCurrentReleaseFeatures();
    }
  }, [currentRelease, loadCurrentReleaseFeatures]);

  const handleCreate = () => {
    setEditingRelease(null);
    setFormData({
      name: '',
      internal_description: '',
      public_description: '',
      start_date: '',
      end_date: '',
      status: 'draft',
      current_subversion: '',
      comments: ''
    });
    setShowEditDialog(true);
  };

  const handleEdit = (release) => {
    setEditingRelease(release);
    setFormData({
      name: release.name,
      internal_description: release.internal_description || '',
      public_description: release.public_description || '',
      start_date: release.start_date || '',
      end_date: release.end_date || '',
      status: release.status || 'draft',
      current_subversion: release.current_subversion || '',
      comments: release.comments || ''
    });
    setShowEditDialog(true);
  };

  const handleDelete = async (releaseId) => {
    if (!window.confirm('Are you sure you want to delete this release?')) {
      return;
    }

    try {
      setLoading(true);
      setError(null);
      await deleteRelease(releaseId);
      await loadReleases();
    } catch (err) {
      setError(handleError(err, 'Failed to delete release. Please try again.'));
      logger.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      setError('Release name is required');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      if (editingRelease) {
        await updateRelease(editingRelease.id, formData);
      } else {
        await createRelease({
          ...formData,
          feature_group_id: featureGroupId
        });
      }
      
      setShowEditDialog(false);
      await loadReleases();
    } catch (err) {
      setError(handleError(err, `Failed to ${editingRelease ? 'update' : 'create'} release. Please try again.`));
      logger.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setShowEditDialog(false);
    setEditingRelease(null);
    setFormData({
      name: '',
      internal_description: '',
      public_description: '',
      start_date: '',
      end_date: '',
      status: 'draft',
      current_subversion: '',
      comments: ''
    });
    setError(null);
  };

  // Memoize total votes calculation to avoid recalculation on every render
  const totalVotes = useMemo(() => {
    let total = 0;
    voteCounts.forEach(count => {
      if (count && typeof count === 'object') {
        total += count.total || 0;
      }
    });
    return total;
  }, [voteCounts]);

  return (
    <div className="releases-container">
      <div className="releases-header">
        <h2>Releases</h2>
        {isOwner && (
          <button
            type="button"
            className="add-release-button"
            onClick={handleCreate}
            disabled={loading}
          >
            <i className="fas fa-plus"></i> Add Release
          </button>
        )}
      </div>

      {error && !showEditDialog && (
        <div className="error-message">
          <i className="fas fa-exclamation-circle"></i> {error}
        </div>
      )}

      {loading && !showEditDialog && releases.length === 0 ? (
        <div className="loading">Loading releases...</div>
      ) : (
        <>
          {currentRelease && (
            <div className="current-release-panel">
              <div className="current-release-header">
                <div className="current-release-header-left">
                  <h3>Current Release</h3>
                  {currentRelease.current_subversion && (
                    <span className="current-subversion-badge">v{currentRelease.current_subversion}</span>
                  )}
                </div>
                {isOwner && (
                  <div className="current-release-actions">
                    <button
                      type="button"
                      className="icon-button"
                      onClick={() => handleEdit(currentRelease)}
                      title="Edit release"
                    >
                      <i className="fas fa-edit"></i>
                    </button>
                    <button
                      type="button"
                      className="icon-button delete-button"
                      onClick={() => handleDelete(currentRelease.id)}
                      title="Delete release"
                    >
                      <i className="fas fa-trash"></i>
                    </button>
                  </div>
                )}
              </div>
              <div className="current-release-content">
                <div className="current-release-left">
                  <div className="current-release-info">
                    <h4>{currentRelease.name}</h4>
                    <div className="release-dates">
                      <span className="date-label">Start:</span> {formatDate(currentRelease.start_date)}
                      {currentRelease.end_date && (
                        <>
                          <span className="date-separator"> - </span>
                          <span className="date-label">End:</span> {formatDate(currentRelease.end_date)}
                        </>
                      )}
                    </div>
                    <div className="release-description">
                      {currentRelease.public_description || currentRelease.internal_description || 'No description'}
                    </div>
                  </div>
                  <div className="current-release-features">
                    <h5>Features ({currentReleaseFeatures.length})</h5>
                    {currentReleaseFeatures.length === 0 ? (
                      <div className="no-features">No features in this release</div>
                    ) : (
                      <table className="release-features-table">
                        <thead>
                          <tr>
                            <th>Feature Name</th>
                            <th>Status</th>
                            <th>Votes</th>
                          </tr>
                        </thead>
                        <tbody>
                          {currentReleaseFeatures.map(feature => (
                              <tr key={feature.id} onClick={() => onViewFeature && onViewFeature(feature)} className="feature-row-clickable">
                                <td>{feature.name}</td>
                                <td>
                                  <span className={`status-pill status-${(feature.status || 'No Status').toLowerCase().replace(/\s+/g, '-')}`}>
                                    {feature.status || 'No Status'}
                                  </span>
                                </td>
                                <td>{voteCounts.get(feature.id)?.total || 0}</td>
                              </tr>
                            ))}
                        </tbody>
                        <tfoot>
                          <tr className="total-votes-row">
                            <td colSpan="2"><strong>Total Votes:</strong></td>
                            <td><strong>{totalVotes}</strong></td>
                          </tr>
                        </tfoot>
                      </table>
                    )}
                  </div>
                </div>
                <div className="current-release-right">
                  <div className="release-comments-section">
                    <h5>Release Comments</h5>
                    <div className="release-comments-content">
                      {currentRelease.comments ? (
                        <pre className="release-comments-text">{currentRelease.comments}</pre>
                      ) : (
                        <div className="no-comments">No comments for this release</div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="releases-list-section">
            <h3>All Releases</h3>
            {releases.length === 0 ? (
              <div className="no-releases">
                {isOwner ? 'No releases yet. Click "Add Release" to create one.' : 'No releases available.'}
              </div>
            ) : (
              <table className="releases-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Status</th>
                    <th>Start/End</th>
                    <th>Features</th>
                    {isOwner && <th>Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {releases
                    .filter(release => !currentRelease || release.id !== currentRelease.id)
                    .map(release => {
                      // Determine if this release should be auto-expanded
                      // Auto-expand the earliest non-Live release by end_date
                      const filteredReleases = releases.filter(r => !currentRelease || r.id !== currentRelease.id);
                      const nonLiveReleases = filteredReleases.filter(r => r.status.toLowerCase() !== 'live' && r.end_date);
                      const earliestNonLiveRelease = nonLiveReleases.length > 0
                        ? nonLiveReleases.reduce((earliest, current) => {
                            const earliestDate = new Date(earliest.end_date);
                            const currentDate = new Date(current.end_date);
                            return currentDate < earliestDate ? current : earliest;
                          })
                        : null;
                      const defaultExpanded = earliestNonLiveRelease && earliestNonLiveRelease.id === release.id;
                      
                      return (
                        <MemoizedReleaseRow
                          key={release.id}
                          release={release}
                          isOwner={isOwner}
                          onEdit={handleEdit}
                          onDelete={handleDelete}
                          onViewFeature={onViewFeature}
                          featureGroupId={featureGroupId}
                          defaultExpanded={defaultExpanded}
                          userRole={userRole}
                        />
                      );
                    })}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}

      {showEditDialog && (
        <div className="edit-dialog-overlay" onClick={handleCancel}>
          <div className="edit-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="edit-dialog-header">
              <h3>{editingRelease ? 'Edit Release' : 'Create Release'}</h3>
              <button
                type="button"
                className="close-button"
                onClick={handleCancel}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>

            {error && (
              <div className="error-message">
                <i className="fas fa-exclamation-circle"></i> {error}
              </div>
            )}

            <form onSubmit={handleSave} className="edit-dialog-form">
              <div className="form-group">
                <label htmlFor="release-name">
                  Name <span className="required">*</span>
                </label>
                <input
                  id="release-name"
                  type="text"
                  value={formData.name}
                  onChange={(e) => {
                    setFormData({ ...formData, name: e.target.value });
                    setError(null);
                  }}
                  placeholder="Enter release name"
                  disabled={loading}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="release-status">Status</label>
                <select
                  id="release-status"
                  value={formData.status}
                  onChange={(e) => {
                    setFormData({ ...formData, status: e.target.value });
                    setError(null);
                  }}
                  disabled={loading}
                >
                  {STATUS_OPTIONS_WITH_NUMBERS.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="release-start-date">Start Date</label>
                <input
                  id="release-start-date"
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => {
                    setFormData({ ...formData, start_date: e.target.value });
                    setError(null);
                  }}
                  disabled={loading}
                />
              </div>

              <div className="form-group">
                <label htmlFor="release-end-date">End Date</label>
                <input
                  id="release-end-date"
                  type="date"
                  value={formData.end_date}
                  onChange={(e) => {
                    setFormData({ ...formData, end_date: e.target.value });
                    setError(null);
                  }}
                  disabled={loading}
                />
              </div>

              <div className="form-group">
                <label htmlFor="release-internal-description">Internal Description</label>
                <textarea
                  id="release-internal-description"
                  value={formData.internal_description}
                  onChange={(e) => {
                    setFormData({ ...formData, internal_description: e.target.value });
                    setError(null);
                  }}
                  placeholder="Internal description (not public)"
                  disabled={loading}
                  rows={4}
                />
              </div>

              <div className="form-group">
                <label htmlFor="release-public-description">Public Description</label>
                <textarea
                  id="release-public-description"
                  value={formData.public_description}
                  onChange={(e) => {
                    setFormData({ ...formData, public_description: e.target.value });
                    setError(null);
                  }}
                  placeholder="Public description"
                  disabled={loading}
                  rows={4}
                />
              </div>

              <div className="form-group">
                <label htmlFor="release-current-subversion">Minor Version</label>
                <input
                  id="release-current-subversion"
                  type="text"
                  value={formData.current_subversion}
                  onChange={(e) => {
                    setFormData({ ...formData, current_subversion: e.target.value });
                    setError(null);
                  }}
                  placeholder="e.g., 1.1.2"
                  disabled={loading}
                />
                <small className="help-text">
                  Minor version number for this release (e.g., 1.1.2)
                </small>
              </div>

              <div className="form-group">
                <label htmlFor="release-comments">Release Comments</label>
                <textarea
                  id="release-comments"
                  value={formData.comments}
                  onChange={(e) => {
                    setFormData({ ...formData, comments: e.target.value });
                    setError(null);
                  }}
                  placeholder="Release notes or comments"
                  disabled={loading}
                  rows={6}
                />
              </div>

              <div className="edit-dialog-actions">
                <button
                  type="button"
                  className="cancel-button"
                  onClick={handleCancel}
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="save-button"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <i className="fas fa-spinner fa-spin"></i> Saving...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-save"></i> Save
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Releases;

